/**
 * Utility functions to detect browser capabilities
 * Helps identify older browsers that need compatibility mode
 */

/**
 * Check if the current browser is considered an older browser
 * Based on browser features rather than specific version checks
 */
export const isOlderBrowser = (): boolean => {
  // Try to detect features that might be missing in older browsers
  try {
    // Check for some modern browser features
    const hasFlexGap = CSS.supports('gap: 1px');
    const hasModernFetch = 'AbortController' in window && 'signal' in AbortController.prototype;
    
    // Check Chrome/Android version via user agent
    // This is not 100% reliable but helps for Android devices
    const userAgent = navigator.userAgent.toLowerCase();
    const chromeMatch = /chrome\/(\d+)/.exec(userAgent);
    const chromeVersion = chromeMatch ? parseInt(chromeMatch[1], 10) : 999;
    const isOlderChrome = chromeVersion < 80; // Chrome 80 was released in early 2020
    
    // Check for Mobile Browser
    const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
      userAgent
    );
    
    // Return true if this is likely an older mobile browser
    return (isMobile && (!hasFlexGap || !hasModernFetch || isOlderChrome));
    
  } catch (error) {
    // If any error occurs during detection, assume it's an older browser
    // since modern browsers shouldn't throw errors for these checks
    console.warn('Error detecting browser capabilities, using compatibility mode:', error);
    return true;
  }
};

/**
 * Get device memory if available (Chrome-only feature)
 * Returns memory in GB or null if not supported
 */
export const getDeviceMemory = (): number | null => {
  try {
    // @ts-ignore - deviceMemory is not in the standard TS types
    return navigator.deviceMemory || null;
  } catch {
    return null;
  }
};

/**
 * Returns true if the device is likely low-end
 */
export const isLowEndDevice = (): boolean => {
  const memory = getDeviceMemory();
  // Consider devices with less than 2GB RAM as low-end
  return memory !== null && memory < 2;
};
